import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Typography, Spin, Flex, App } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { Container } from '../../../components/CssFrameworkComponents';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChargeBeePlanId } from '@prerender/billing-shared';

import BillingChangePlanConfirmModal from './BillingChangePlanConfirmModal';
import { initChargebee } from '../../../chargebee/chargebee';
import {
  useChangePlanMutation,
  useGetScheduledChangeQuery,
  useGetPlansQuery,
  useSchedulePlanChangeMutation,
  useUnschedulePlanCancellationMutation,
} from '../api/billingApiSlice';
import { getPrerenderUser, prerenderUserPatch } from '../../../actions/prerenderUserActions';
import { useEvent } from '../../events/hooks/useEvent';
import { usePrerenderUser, useIsSubscriptionCancelled } from '../../../hooks/usePrerenderUser';
import InsufficientRendersDisclaimer from './InsufficientRendersDisclaimer';
import { useTrial } from '~features/FreeTrial/useTrial';
import { dates } from '~library/dates';
import BillingPlansList from './BillingPlansList';
import { isStarterTrialKeepPlanScenario, isImmediateStarterTrialActivation } from './starterTrialActivation';
import Plan from './Plan';
import FeedbackPlanChangedModal from './FeedbackPlanChangedModal';
import { URL_FROM_PARAMS, URLFromParams } from '../../../utils/UrlFromParams';
import { useSubscriptionState } from '../PlanStatus';

export function calcInsufficientRenders(currentRenders: number, newPlan: Plan) {
  return currentRenders >= newPlan.includedCaches;
}

const BillingPlans = () => {
  const [showFeedbackConvertedModal, setShowFeedbackConvertedModal] = useState(false);
  const [planChangeIds, setPlanChangeIds] = useState<{ old: ChargeBeePlanId; new: ChargeBeePlanId } | undefined>();

  const { message } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isInTrial, discountAvailable } = useTrial();
  const [planState, paymentMethodState] = useSubscriptionState();

  const [showPlansOverlay, setShowPlansOverlay] = useState(false);
  const [pendingNewPlan, setPendingNewPlan] = useState<Plan | null>(null);
  const [showInsufficientRendersModal, setShowInsufficientRendersModal] = useState(false);
  const [showChangePlanConfirmModal, setShowChangePlanConfirmModal] = useState(false);
  // Whether the user already had valid payment details when the confirm modal opened. A return visit
  // (payment already on file) drops "Activate after trial" so only "Activate now" remains; the first
  // visit (adding payment during this visit) keeps both. Captured once per open, reset on close, so
  // adding a card mid-visit doesn't retract the after-trial option (PRE-2726 QA).
  const [chargeableWhenModalOpened, setChargeableWhenModalOpened] = useState<boolean | null>(null);

  const user = usePrerenderUser();
  const isSubscriptionCancelled = useIsSubscriptionCancelled();
  const isUserOnCustomPlan = typeof user.customPrice === 'number';

  const scheduledChangeResult = useGetScheduledChangeQuery();
  const {
    data: plansListResponse,
    isFetching: isLoadingPlans,
    isError: isErrorLoadingPlans,
  } = useGetPlansQuery(undefined, { refetchOnMountOrArgChange: true });
  const [changePlan, { isLoading: changePlanInProgress }] = useChangePlanMutation();
  const [schedulePlanChange, { isLoading: schedulePlanChangeInProgress }] = useSchedulePlanChangeMutation();
  const [unschedulePlanCancellation, { isLoading: unschedulePlanCancellationInProgress }] =
    useUnschedulePlanCancellationMutation();
  const { track } = useEvent();

  const source = searchParams.get('utm_source');

  const plansList = plansListResponse?.data;
  const currentPlan = user.plan.current;
  const preventSamePlanSelection = planState.status === 'active' && paymentMethodState.isChargeable && !isInTrial;

  const currentRenders = useMemo(
    () => Math.floor(user.numPagesCached * (30 / user.cacheFreshness)),
    [user.numPagesCached, user.cacheFreshness]
  );

  const onPlanClicked = (newPlan: Plan) => {
    const newSearchParams = new URLSearchParams();
    newSearchParams.set('selectedPlan', newPlan.id);

    const fromParam = searchParams.get('from');
    if (fromParam && fromParam in URL_FROM_PARAMS) {
      newSearchParams.set('from', fromParam);
    }

    navigate(`?${newSearchParams.toString()}`);
  };

  const onPlanUpdateConfirm = () => {
    setShowInsufficientRendersModal(false);
    if (pendingNewPlan) handleSelectPlan(pendingNewPlan);
  };

  const onPlanUpdateCancel = () => navigate('');

  const handleSelectPlan = useCallback(
    (newPlan: Plan) => {
      track('Subscription Plan Selected', {
        subscription_plan: currentPlan?.name,
        selected_subscription_plan: newPlan.name,
        selected_subscription_price: newPlan.costInCents / 100,
        source: source,
      });

      if (isUserOnCustomPlan) {
        setShowPlansOverlay(true);
      } else {
        setShowChangePlanConfirmModal(true);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- track is stable (cached by useEvent)
    [currentPlan, isUserOnCustomPlan, source]
  );

  const handleStartPlan = async (oldPlanId: ChargeBeePlanId, newPlanId: ChargeBeePlanId, activateNow?: boolean) => {
    // Capture the trial state before the mutation — redux flips isInTrial after a successful activation.
    const wasInTrialBeforeActivation = isInTrial;

    try {
      await changePlan({
        planId: newPlanId,
        applyFreeTrialCoupon: !!discountAvailable,
        unschedulePlanCancellation: true,
        activateNow,
      }).unwrap();

      if (wasInTrialBeforeActivation && activateNow === true) {
        track('Starter Trial Activated', {
          previous_state: 'in-trial',
          new_state: 'paid active',
          subscription_plan: pendingNewPlan?.name ?? newPlanId,
          activated_at: new Date().toISOString(),
        });
      }

      // if the plan change ids are set and the new plan is different from the old plan, show the feedback modal
      if (oldPlanId && newPlanId && oldPlanId !== newPlanId) {
        setPlanChangeIds({ old: oldPlanId, new: newPlanId });
        setShowFeedbackConvertedModal(true);
        return;
      }

      // After activating paid Starter during the trial, refetch the user so paid-plan fields (price,
      // next billing date, last charged, etc.) reflect paid Starter on the Overview rather than the
      // pre-activation trial summary (PRE-2726 QA #3). Dispatched before the synchronous patch below so
      // the patch — which flips hasTrialEnded instantly, before the refetch resolves — stays applied.
      if (wasInTrialBeforeActivation) {
        dispatch(getPrerenderUser());
      }
      // plan reactivated (same plan + fully cancelled) or paid Starter activated immediately during trial.
      // Clearing the trial fields (only when the user was in trial) flips hasTrialEnded immediately so the
      // Overview reflects paid Starter without a manual refresh — the backend already ended the trial
      // synchronously (in_trial=false, trial_ends_at=now).
      dispatch(
        prerenderUserPatch({
          recachingDisabled: false,
          cancelledAt: null,
          renderCounter: 0,
          ...(wasInTrialBeforeActivation && { isTrial: false, trialEndsAt: new Date() }),
        })
      );
      navigate('/billing/overview');
    } catch (error) {
      let errorMessage = 'Please contact support@prerender.io.';

      if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      message.open({
        type: 'error',
        content: `Unable to change plan. ${errorMessage}`,
      });
    }
  };

  const handleSchedulePlanChange = async (newPlanId: ChargeBeePlanId) => {
    try {
      await schedulePlanChange({
        planId: newPlanId,
        replaceScheduledActionConfirmed:
          planState.status === 'cancellation_scheduled' || Boolean(scheduledChangeResult.data),
      }).unwrap();

      message.open({
        type: 'success',
        content: 'Your downgrade has been scheduled for the end of your current billing period.',
      });
      setShowChangePlanConfirmModal(false);
      navigate('');
    } catch (error) {
      let errorMessage = 'Please contact support@prerender.io.';

      if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
        errorMessage = error.message;
      }

      message.open({
        type: 'error',
        content: `Unable to schedule plan change. ${errorMessage}`,
      });
    }
  };

  const handleKeepPlan = async () => {
    try {
      if (planState.status === 'cancellation_scheduled') {
        await unschedulePlanCancellation().unwrap();
      }

      dispatch(prerenderUserPatch({ recachingDisabled: false, cancelledAt: null }));
      navigate('/billing/overview');
    } catch (error) {
      message.open({
        type: 'error',
        content: 'Unable to change plan. Please contact support@prerender.io.',
      });
    }
  };

  const handleConfirmPlanChange = (oldPlanId: ChargeBeePlanId, newPlanId: ChargeBeePlanId, activateNow?: boolean) => {
    const hasPlanChanged = oldPlanId !== newPlanId;
    const isSelectedPlanDowngrade =
      currentPlan != null && pendingNewPlan != null && pendingNewPlan.index < currentPlan.index;

    if (isImmediateStarterTrialActivation({ oldPlanId, newPlanId, isInTrial, activateNow })) {
      return handleStartPlan(oldPlanId, newPlanId, true);
    }

    // The keep-trial branch safely precedes the cancelled-reactivation branches below:
    // the backend sets in_trial=false atomically with recaching_disabled/cancelled_at, so
    // isInTrial and isSubscriptionCancelled are never both true (see OnSubscriptionCancelled).
    if (isStarterTrialKeepPlanScenario({ oldPlanId, newPlanId, isInTrial })) {
      return handleKeepPlan();
    }

    if (hasPlanChanged && isSubscriptionCancelled) {
      return handleStartPlan(oldPlanId, newPlanId);
    }

    if (hasPlanChanged && isSelectedPlanDowngrade) {
      return handleSchedulePlanChange(newPlanId);
    }

    if (hasPlanChanged || isSubscriptionCancelled) {
      return handleStartPlan(oldPlanId, newPlanId);
    }

    return handleKeepPlan();
  };

  // Return to the page the user came from when it's declared via ?from= (e.g. the Billing
  // Overview CTAs); otherwise stay on the plans list.
  const navigateBackToOrigin = useCallback(() => {
    const fromParam = searchParams.get('from');
    if (fromParam && fromParam in URL_FROM_PARAMS) {
      navigate(URL_FROM_PARAMS[fromParam as URLFromParams]);
    } else {
      navigate('');
    }
  }, [searchParams, navigate]);

  const handleCancelPlanChange = useCallback(() => {
    setShowChangePlanConfirmModal(false);
    navigateBackToOrigin();
    track('Subscription Update Flow Cancelled', { subscription_plan: currentPlan?.name, source: source });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- track is stable (cached by useEvent)
  }, [currentPlan, navigateBackToOrigin, source]);

  const scheduledPlanName = useMemo(() => {
    if (!scheduledChangeResult.data) return undefined;

    const scheduledPlan = plansList?.find((plan) => plan.id === scheduledChangeResult.data?.planId);
    return scheduledPlan?.name;
  }, [scheduledChangeResult.data, plansList]);

  const displayPlanChangeScheduled = scheduledChangeResult.data;

  const onFeedbackPlanChangedModalClosed = () => {
    setShowFeedbackConvertedModal(false);
    setPlanChangeIds(undefined);

    dispatch(getPrerenderUser());

    navigateBackToOrigin();
  };

  useEffect(() => {
    const url = window.location.href;
    initChargebee(url);
  }, []);

  // An effect rather than a derived const on purpose: the snapshot must (1) wait for the
  // network-backed payment state to settle (isLoading) before reading it, and (2) then stay frozen —
  // a const would recompute every render and retract "Activate after trial" the moment isChargeable
  // flips after the user adds a card mid-visit. Reset on close so the next open re-captures.
  useEffect(() => {
    if (!showChangePlanConfirmModal) {
      if (chargeableWhenModalOpened !== null) setChargeableWhenModalOpened(null);
      return;
    }
    if (chargeableWhenModalOpened === null && !paymentMethodState.isLoading) {
      setChargeableWhenModalOpened(paymentMethodState.isChargeable);
    }
  }, [
    showChangePlanConfirmModal,
    paymentMethodState.isLoading,
    paymentMethodState.isChargeable,
    chargeableWhenModalOpened,
  ]);

  useEffect(() => {
    const selectedPlanId = searchParams.get('selectedPlan');
    const selectedPlan = plansList?.find((plan) => plan.id === selectedPlanId);

    const isCurrentActivePlan = selectedPlan?.id === user.chargebeePlanId && preventSamePlanSelection;
    // prevent the user from selecting the same plan if it is active and the payment method is valid
    if (isCurrentActivePlan) {
      navigate('/billing/plans');
      return;
    }

    if (selectedPlan) {
      const hasInsufficientRenders = calcInsufficientRenders(currentRenders, selectedPlan);

      setPendingNewPlan(selectedPlan);
      if (!hasInsufficientRenders) {
        handleSelectPlan(selectedPlan);
      } else {
        setShowInsufficientRendersModal(true);
      }
    } else {
      setPendingNewPlan(null);
      setShowInsufficientRendersModal(false);
    }
  }, [
    searchParams,
    plansList,
    currentRenders,
    user.chargebeePlanId,
    preventSamePlanSelection,
    handleSelectPlan,
    navigate,
  ]);

  if (isLoadingPlans) {
    return (
      <Flex justify="center" aria-label="Loading plans">
        <Spin size="large" />
      </Flex>
    );
  }

  const isDowngrading = !pendingNewPlan || !currentPlan ? false : pendingNewPlan.index < currentPlan.index;

  return (
    <Container addClass={'p-0'}>
      <InsufficientRendersDisclaimer
        newPlan={showInsufficientRendersModal ? pendingNewPlan : undefined}
        onConfirm={onPlanUpdateConfirm}
        onCancel={onPlanUpdateCancel}
        isDowngrading={isDowngrading}
      />

      {displayPlanChangeScheduled && (
        <Alert
          showIcon
          type="info"
          message={
            <Typography.Text>
              You have a scheduled plan change
              {scheduledPlanName ? (
                <>
                  {' '}
                  to <Typography.Text strong>{scheduledPlanName}</Typography.Text>
                </>
              ) : null}{' '}
              that will be effective on{' '}
              {scheduledChangeResult.data && dates.dayOfPointInTime(scheduledChangeResult.data.changeAt)}
            </Typography.Text>
          }
          style={{ marginBottom: '24px' }}
        />
      )}

      {discountAvailable && (
        <Alert
          showIcon
          type="info"
          message={
            <Typography.Text>
              You have a discount of <Typography.Text strong>{discountAvailable.percentage}%</Typography.Text> for{' '}
              {discountAvailable.months} months!
            </Typography.Text>
          }
          style={{ marginBottom: '24px' }}
        />
      )}

      {showChangePlanConfirmModal && pendingNewPlan ? (
        <BillingChangePlanConfirmModal
          pendingNewPlan={pendingNewPlan}
          currentPlan={currentPlan}
          onConfirmPlanChange={handleConfirmPlanChange}
          onClose={handleCancelPlanChange}
          isLoading={changePlanInProgress || schedulePlanChangeInProgress || unschedulePlanCancellationInProgress}
          scheduledPlanName={scheduledPlanName}
          showActivateAfterTrial={chargeableWhenModalOpened !== true}
        />
      ) : (
        <>
          <div className="d-flex justify-content-between">
            <h3 className="">Prerender Plans</h3>
          </div>
          <p className="text-base text-grey">Select an account plan that fits your needs.</p>

          <div className="position-relative">
            {showPlansOverlay && (
              <div className="plans-overlay">
                <div className="plans-overlay-content">
                  <div className="d-flex justify-content-end">
                    <button className="btn p-0" onClick={() => setShowPlansOverlay(false)}>
                      <CloseOutlined />
                    </button>
                  </div>
                  <p className="font-weight-bold text-lg">You&apos;re on a Custom plan!</p>
                  <span>
                    Your account runs on a custom plan, so you are not able to change your plan via this page. If you
                    want to re-negotiate your plan, please send us an email at
                  </span>{' '}
                  <a className="font-weight-bold" href="mailto:billing@prerender.io">
                    billing@prerender.io
                  </a>
                  <span>.</span>
                </div>
              </div>
            )}
            {isErrorLoadingPlans ? (
              <p style={{ color: 'red' }}>
                An unexpected error occurred when retrieving the plans. Please try again by reloading the page.
              </p>
            ) : (
              <BillingPlansList plans={plansList} onPlanSelected={onPlanClicked} />
            )}
          </div>
        </>
      )}
      {planChangeIds && (
        <FeedbackPlanChangedModal
          open={showFeedbackConvertedModal}
          planIds={planChangeIds}
          onClose={onFeedbackPlanChangedModalClosed}
        />
      )}
    </Container>
  );
};

export default BillingPlans;
